// ─── Marker clustering ────────────────────────────────────────────────────────
// Markers (milestones, key dates) are positioned by date. At coarse zooms several
// can land within a few pixels of each other and become unreadable / un-clickable.
// `clusterByX` groups neighbours within a pixel threshold so the row can render a
// single count-badged marker in their place.

export interface GanttPositioned<T> {
  item: T;
  /** x offset in pixels from the timeline origin. */
  x: number;
}

export interface GanttCluster<T> {
  /** Mean x of the clustered items (px from timeline origin). */
  x: number;
  items: T[];
}

/**
 * Groups positioned markers into clusters: items are sorted by x and a new cluster
 * starts whenever the gap to the previous item exceeds `threshold` px. Each cluster
 * is anchored at the mean x of its members. A lone marker yields a single-item
 * cluster, so callers can treat singles and clusters uniformly.
 */
export const clusterByX = <T>(positioned: GanttPositioned<T>[], threshold: number): GanttCluster<T>[] => {
  const sorted = [...positioned].sort((a, b) => a.x - b.x);
  const groups: GanttPositioned<T>[][] = [];
  sorted.forEach((p) => {
    const current = groups[groups.length - 1];
    const prev = current?.[current.length - 1];
    if (current && prev && p.x - prev.x <= threshold) {
      current.push(p);
    } else {
      groups.push([p]);
    }
  });
  return groups.map((group) => ({
    x: group.reduce((sum, p) => sum + p.x, 0) / group.length,
    items: group.map((p) => p.item),
  }));
};
